import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useAuthStore } from '@/store/auth-store';
import { LogOut, BookOpen, Users, Gift, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type StoryOwnership = 'own_story' | 'loved_one' | 'gift_setup';

const storyOptions: {
  value: StoryOwnership;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'own_story',
    label: 'My Own Story',
    description: 'I want to capture and preserve my own life story.',
    icon: <BookOpen className="h-6 w-6" />,
  },
  {
    value: 'loved_one',
    label: "A Loved One's Story",
    description: "I want to help capture someone else's story.",
    icon: <Users className="h-6 w-6" />,
  },
  {
    value: 'gift_setup',
    label: 'Gift a Story',
    description: 'I want to gift the LegacyOrb experience to someone.',
    icon: <Gift className="h-6 w-6" />,
  },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const user = useAuthStore((state) => state.user);
  const [selectedOwnership, setSelectedOwnership] = useState<StoryOwnership | null>(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in');
  };

  const handleContinue = () => {
    if (!selectedOwnership) return;
    // TODO: Save onboarding selection and proceed to next step
    // For now, navigate to a confirmation or next onboarding step
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex items-center justify-between border-b px-6 py-4">
        <h1 className="text-xl font-bold">LegacyOrb</h1>
        <Button variant="ghost" size="sm" onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </header>

      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold">
              Welcome{user?.firstName ? `, ${user.firstName}` : ''}!
            </h2>
            <p className="mt-2 text-muted-foreground">
              Let's get started. How would you like to use LegacyOrb?
            </p>
          </div>

          <div className="grid gap-4">
            {storyOptions.map((option) => (
              <Card
                key={option.value}
                className={`cursor-pointer transition-colors hover:border-primary ${
                  selectedOwnership === option.value ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => setSelectedOwnership(option.value)}
              >
                <CardHeader className="flex flex-row items-center gap-4 pb-2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {option.icon}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{option.label}</CardTitle>
                    <CardDescription>{option.description}</CardDescription>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Button size="lg" disabled={!selectedOwnership} onClick={handleContinue}>
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
